import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { useInterventionCreation } from '../../context/interventionCreationContext';
import {
  AssignTechnicianResult,
  BatchReport,
  buildAssignTechnicianPayload,
  buildAssignTechnicianUrl
} from './assignment';

export type AssignTechnicianState = {
  assignTechnician: (technicianId: number) => void;
  isSaving: boolean;
  hasFailed: boolean;
  /** Bilan du lot, renseigné seulement quand le back a refusé au moins une demande. */
  report: BatchReport | null;
  dismissReport: () => void;
};

/**
 * Affecte un technicien aux demandes cochées dès sa sélection, sans attendre de bouton
 * de validation. Les demandes restent dans cette liste : seul le technicien change.
 *
 * Une affectation partiellement refusée répond 200 : sans lire `failed_ids` / `errors`,
 * l'écran annoncerait un succès alors que rien n'a été affecté (APPSUPPORT-2130).
 */
export function useAssignTechnician(): AssignTechnicianState {
  const { state } = useInterventionCreation();
  const queryClient = useQueryClient();

  const [report, setReport] = useState<BatchReport | null>(null);

  const mutation = useMutation<AssignTechnicianResult, unknown, number>({
    mutationFn: async (technicianId) => {
      if (state.clientId === null) {
        throw new Error('Aucun client sélectionné');
      }

      const response = await axios.post(
        buildAssignTechnicianUrl(state.clientId),
        buildAssignTechnicianPayload(technicianId, state.selectedRequestIds)
      );

      return response.data;
    },
    onSuccess: (result) => {
      // La colonne Technicien de la table lit `technician_data`.
      queryClient.invalidateQueries(['exocet-pds-to-simple-assign']);

      // Mêmes gardes que les transitions de statut : ne pas planter sur une réponse muette.
      const failedIds = result?.failed_ids ?? [];
      const failures = result?.errors ?? [];

      // Un lot entièrement affecté ne mérite pas de bandeau : l'affectation est immédiate
      // et la table se rafraîchit déjà.
      setReport(
        failedIds.length === 0
          ? null
          : {
              succeededCount: result?.success_count ?? 0,
              failedCount: failedIds.length,
              failures
            }
      );
    }
  });

  return {
    assignTechnician: (technicianId) => mutation.mutate(technicianId),
    isSaving: mutation.isLoading,
    hasFailed: mutation.isError,
    report,
    dismissReport: () => setReport(null)
  };
}
